import ImageControl from "@/components/ImageControll";
import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ExtendedDataBerkas, useDataValidasi } from "@/store/dataValidasiStore";
import { DataBerkas as DataBerkasType, SysBerkas } from "@/types";
import { Eye, EyeOff, FileText, Info } from "lucide-react";
import { useState } from "react";

export default function DataBerkas({
  isPending,
  isValidPermanen,
}: {
  isPending: boolean;
  isValidPermanen: boolean;
}) {
  const { dataListBerkas, setDataListBerkas } = useDataValidasi();
  const [showPdf, setShowPdf] = useState(false);
  const [selectedBerkas, setSelectedBerkas] = useState<
    DataBerkasType | null | undefined
  >(null);

  const handleValidasi = (id: number) => {
    const filteredListBerkas = dataListBerkas?.listSysBerkas?.filter(
      (sysBerkas) => {
        return sysBerkas.jenis_berkas?.slug_jenis_berkas != "Surat_Pernyataan";
      },
    );

    const updatedData = dataListBerkas &&
      filteredListBerkas && {
        listSysBerkas: filteredListBerkas?.map((item) =>
          item.id_sys_berkas === id
            ? { ...item, isValidated: !item.isValidated }
            : item,
        ),
        listDataBerkasSiswa: dataListBerkas.listDataBerkasSiswa,
      };

    setDataListBerkas(updatedData as ExtendedDataBerkas);

    const dataValidasiString = localStorage.getItem("dataValidasi");
    const previousData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : {};
    const previousBiodata = previousData.biodata || [];
    const previousKeluarga = previousData.keluarga || [];
    const previousKeluargaTambahan = previousData.keluarga_tambahan || null;
    const previousAset = previousData.aset || [];

    const updatedDataValidasi = {
      biodata: previousBiodata,
      keluarga: previousKeluarga,
      keluarga_tambahan: previousKeluargaTambahan,
      aset: previousAset,
      berkas: updatedData,
    };

    localStorage.setItem("dataValidasi", JSON.stringify(updatedDataValidasi));
  };

  const uploadedListSysBerkas =
    dataListBerkas?.listSysBerkas?.filter((item) => {
      return dataListBerkas?.listDataBerkasSiswa?.some((berkas) => {
        return berkas.id_sys_berkas == String(item.id_sys_berkas);
      });
    }) || [];

  const filteredlistBerkas = uploadedListSysBerkas
    ?.filter(
      (sysBerkas: SysBerkas) =>
        sysBerkas.jenis_berkas?.slug_jenis_berkas !== "Surat_Pernyataan",
    )
    .sort(
      (a: SysBerkas, b: SysBerkas) => Number(b.is_wajib) - Number(a.is_wajib),
    );

  if (isPending)
    return (
      <div className="flex flex-col gap-y-3">
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <SkeletonRow key={index} />
          ))}
      </div>
    );

  return (
    <section className="p-2 sm:p-5 flex flex-col gap-4 flex-1">
      {/* HEADER SECTION */}
      <div className="flex items-center gap-2 px-1 mb-2">
        <FileText className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
          Daftar Dokumen Pendukung
        </h2>
      </div>

      {/* --- TAMPILAN MOBILE (CARD) --- */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {filteredlistBerkas?.length > 0 ? (
          filteredlistBerkas.map((item, index) => {
            const berkasSiswa = dataListBerkas?.listDataBerkasSiswa.find(
              (berkas: DataBerkasType) =>
                berkas?.id_sys_berkas == String(item.id_sys_berkas),
            );
            const isUploaded = !!berkasSiswa;

            return (
              <div
                key={index}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 flex flex-col gap-3"
              >
                {/* Header Card: Jenis Dokumen & Checkbox Validasi */}
                <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 pr-3 leading-snug">
                    {item.jenis_berkas?.nama_jenis_berkas}
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                      Validasi:
                    </span>
                    <Checkbox
                      className={`h-5 w-5 rounded-md transition-colors ${
                        isValidPermanen || item.isValidated
                          ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          : ""
                      }`}
                      checked={isValidPermanen || item.isValidated}
                      onCheckedChange={() =>
                        !isValidPermanen && handleValidasi(item.id_sys_berkas)
                      }
                      disabled={!isUploaded}
                    />
                  </label>
                </div>

                {/* Body Card: Keterangan */}
                <div>
                  <p className="text-xs text-slate-500 mb-1">Keterangan</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {item?.keterangan_berkas || "-"}
                  </p>
                </div>

                {/* Footer Card: Tombol Lihat Berkas */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 mt-1">
                  <Button
                    type="button"
                    variant={isUploaded ? "outline" : "ghost"}
                    size="sm"
                    disabled={!isUploaded}
                    className={`w-full h-9 ${!isUploaded && "opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/50"}`}
                    onClick={() => {
                      if (isUploaded) {
                        setShowPdf(true);
                        setSelectedBerkas(berkasSiswa);
                      }
                    }}
                  >
                    {isUploaded ? (
                      <>
                        <Eye className="w-4 h-4 mr-2 text-primary" /> Lihat
                        Berkas
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" /> Berkas Kosong
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center p-8 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500">
            <FileText className="w-10 h-10 mb-3 mx-auto opacity-20" />
            <p className="text-sm">Tidak ada dokumen yang dipersyaratkan.</p>
          </div>
        )}
      </div>

      {/* --- TAMPILAN DESKTOP (TABEL) --- */}
      <div className="hidden md:block bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Jenis Dokumen
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Keterangan
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Berkas
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap w-[8rem]">
                  Validasi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {filteredlistBerkas?.length > 0 ? (
                filteredlistBerkas?.map((item, index) => {
                  const berkasSiswa = dataListBerkas?.listDataBerkasSiswa.find(
                    (berkas: DataBerkasType) =>
                      berkas?.id_sys_berkas == String(item.id_sys_berkas),
                  );
                  const isUploaded = !!berkasSiswa;

                  return (
                    <tr
                      key={index}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                        {item.jenis_berkas?.nama_jenis_berkas}
                      </td>
                      <td
                        className="px-4 py-3 text-slate-600 dark:text-slate-400 max-w-xs truncate"
                        title={item?.keterangan_berkas}
                      >
                        {item?.keterangan_berkas || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          type="button"
                          variant={isUploaded ? "outline" : "ghost"}
                          size="sm"
                          disabled={!isUploaded}
                          className={`h-8 ${!isUploaded && "opacity-50 cursor-not-allowed"}`}
                          onClick={() => {
                            if (isUploaded) {
                              setShowPdf(true);
                              setSelectedBerkas(berkasSiswa);
                            }
                          }}
                        >
                          {isUploaded ? (
                            <>
                              <Eye className="w-4 h-4 mr-2 text-primary" />{" "}
                              Lihat
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4 mr-2" /> Kosong
                            </>
                          )}
                        </Button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center h-full">
                          <Checkbox
                            className={`h-6 w-6 rounded-md transition-colors ${
                              isValidPermanen || item.isValidated
                                ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                : ""
                            }`}
                            checked={isValidPermanen || item.isValidated}
                            onCheckedChange={() =>
                              !isValidPermanen &&
                              handleValidasi(item.id_sys_berkas)
                            }
                            disabled={!isUploaded}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <FileText className="w-10 h-10 mb-3 opacity-20" />
                      <p>Tidak ada dokumen yang dipersyaratkan.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ==========================================
          MODAL PDF VIEWER / IMAGE
      ========================================== */}
      <Modal
        show={showPdf}
        onClose={() => setShowPdf(false)}
        className="w-[95vw] lg:w-[65vw] h-[85vh] p-0 overflow-hidden flex flex-col bg-slate-100 dark:bg-slate-900"
      >
        {(selectedBerkas?.nomor ||
          selectedBerkas?.sys_berkas?.keterangan_berkas) && (
          <div className="flex flex-col gap-3 px-5 py-4 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shrink-0 shadow-sm z-10">
            {selectedBerkas?.nomor && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Nomor Dokumen:
                </span>
                <Badge
                  variant="outline"
                  className="font-mono text-sm bg-primary/5 text-primary border-primary/20 px-2.5 py-0.5"
                >
                  {selectedBerkas.nomor}
                </Badge>
              </div>
            )}
            {selectedBerkas?.sys_berkas?.keterangan_berkas && (
              <div className="flex items-start gap-2.5 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/50 p-3 rounded-xl">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-blue-800 dark:text-blue-400 mb-0.5 uppercase tracking-wide">
                    Keterangan
                  </span>
                  <p className="text-sm text-blue-700/90 dark:text-blue-300/90 leading-relaxed">
                    {selectedBerkas.sys_berkas.keterangan_berkas}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 w-full relative bg-slate-100 dark:bg-slate-900">
          {selectedBerkas?.path_berkas?.toLowerCase().endsWith(".pdf") ? (
            <PdfViewer url={selectedBerkas?.path_berkas} />
          ) : (
            <ImageControl imageUrl={selectedBerkas?.path_berkas || ""} />
          )}
        </div>
      </Modal>
    </section>
  );
}
